import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./Shop.css";

const MAYACHITRO_BRAND = "mayachitro";

function publicAsset(path) {
  return buildPublicFileUrl(path);
}

const SHOP_HERO = {
  avif: publicAsset("/uploads/public/hero/shop-hero-mayachitro.avif"),
  webp: publicAsset("/uploads/public/hero/shop-hero-mayachitro.webp"),
  png: publicAsset("/uploads/public/hero/shop-hero-mayachitro.png"),
};

const SHOP_HERO_IMAGE_SET = `image-set(
  url("${SHOP_HERO.avif}") type("image/avif"),
  url("${SHOP_HERO.webp}") type("image/webp"),
  url("${SHOP_HERO.png}") type("image/png")
)`;

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function prettifySlug(value) {
  if (!value) return "";

  return String(value)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getValue(product, keys = []) {
  for (const key of keys) {
    if (
      product?.[key] !== undefined &&
      product?.[key] !== null &&
      product?.[key] !== ""
    ) {
      return product[key];
    }
  }

  return "";
}

function getProductBrandSlug(product) {
  return normalizeSlug(
    getValue(product, ["brand_slug", "brandSlug", "brand"])
  );
}

function getProductCategorySlug(product) {
  return normalizeSlug(
    getValue(product, ["category_slug", "categorySlug", "category"])
  );
}

function getProductCollectionSlug(product) {
  return normalizeSlug(
    getValue(product, [
      "collection_slug",
      "collectionSlug",
      "collection_name",
      "collectionName",
      "collection",
    ])
  );
}

function getProductSearchText(product) {
  const tags = Array.isArray(product?.tags)
    ? product.tags
        .map((tag) => {
          if (typeof tag === "string") return tag;
          return tag?.name || tag?.tag || tag?.slug || tag?.title || "";
        })
        .join(" ")
    : "";

  return normalizeText(
    [
      product?.id,
      product?.productId,
      product?.product_id,
      product?.productCode,
      product?.product_code,
      product?.sku,
      product?.slug,
      product?.title,
      product?.name,
      product?.brand,
      product?.brandSlug,
      product?.brand_slug,
      product?.category,
      product?.categoryName,
      product?.category_name,
      product?.categorySlug,
      product?.category_slug,
      product?.collection,
      product?.collectionName,
      product?.collection_name,
      product?.collectionSlug,
      product?.collection_slug,
      product?.shortDescription,
      product?.short_description,
      product?.description,
      product?.mood,
      product?.location_name,
      tags,
    ].join(" ")
  );
}

function productMatchesUrlFilters(product, filters) {
  const { category, collection } = filters;

  const brandMatches =
    getProductBrandSlug(product) === MAYACHITRO_BRAND ||
    !getProductBrandSlug(product);

  const categoryMatches = category
    ? getProductCategorySlug(product) === normalizeSlug(category)
    : true;

  const collectionMatches = collection
    ? getProductCollectionSlug(product) === normalizeSlug(collection)
    : true;

  return brandMatches && categoryMatches && collectionMatches;
}

function buildProductQuery({ category, collection }) {
  const params = new URLSearchParams();

  params.set("status", "published");
  params.set("brand", MAYACHITRO_BRAND);

  if (category) params.set("category", category);
  if (collection) params.set("collection", collection);

  const query = params.toString();

  return query ? `?${query}` : "";
}

function getPrice(product) {
  const value =
    product?.salePrice ||
    product?.sale_price ||
    product?.personal_sale_price ||
    product?.personalPrice ||
    product?.personal_price ||
    product?.price ||
    0;

  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}

function getCreatedTime(product) {
  const value =
    product?.created_at ||
    product?.createdAt ||
    product?.updated_at ||
    product?.updatedAt ||
    0;

  const time = new Date(value).getTime();

  return Number.isFinite(time) ? time : 0;
}

function getProductListFromApiResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.products)) return data.products;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawBrandParam = searchParams.get("brand") || "";
  const categoryParam = searchParams.get("category") || "";
  const collectionParam = searchParams.get("collection") || "";
  const queryParam = searchParams.get("q") || "";
  const sortParam = searchParams.get("sort") || "newest";

  const hidePriceParam =
    searchParams.get("hidePrice") === "1" ||
    searchParams.get("hide_price") === "1";

  const isWrongBrand =
    rawBrandParam && normalizeSlug(rawBrandParam) !== MAYACHITRO_BRAND;

  const hasActiveUrlFilter = Boolean(categoryParam || collectionParam);

  const [products, setProducts] = useState([]);
  const [searchText, setSearchText] = useState(queryParam);
  const [category, setCategory] = useState(categoryParam);
  const [sortBy, setSortBy] = useState(sortParam);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const urlFilters = useMemo(
    () => ({
      category: categoryParam,
      collection: collectionParam,
    }),
    [categoryParam, collectionParam]
  );

  useEffect(() => {
    if (!isWrongBrand) return;

    const next = {};

    if (categoryParam) next.category = categoryParam;
    if (collectionParam) next.collection = collectionParam;

    setSearchParams(next, { replace: true });
  }, [isWrongBrand, categoryParam, collectionParam, setSearchParams]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const query = buildProductQuery({
          category: categoryParam,
          collection: collectionParam,
        });

        const response = await fetch(buildApiUrl(`/products${query}`), {
          headers: { Accept: "application/json" },
        });

        const data = await response.json();

        if (!response.ok || data?.success === false) {
          throw new Error(data?.message || "Failed to load products.");
        }

        const productList = getProductListFromApiResponse(data);

        const publishedMayaChitroProducts = productList.filter((item) => {
          const isPublished =
            !item.status || String(item.status).toLowerCase() === "published";

          const brandSlug = getProductBrandSlug(item);

          const isMayaChitro = brandSlug === MAYACHITRO_BRAND || !brandSlug;

          return isPublished && isMayaChitro;
        });

        setProducts(publishedMayaChitroProducts);
      } catch (err) {
        setError(err.message || "Could not load products from database.");
      } finally {
        setLoading(false);
      }
    };

    setCategory(categoryParam);
    setSearchText(queryParam);
    setSortBy(sortParam);
    fetchProducts();
  }, [categoryParam, collectionParam, queryParam, sortParam]);

  const filteredProducts = useMemo(() => {
    const term = normalizeText(searchText);

    let result = products.filter((product) => {
      const matchesUrlFilters = productMatchesUrlFilters(product, urlFilters);

      const matchesSelectedCategory = category
        ? getProductCategorySlug(product) === normalizeSlug(category)
        : true;

      const matchesSearch = term
        ? getProductSearchText(product).includes(term)
        : true;

      return matchesUrlFilters && matchesSelectedCategory && matchesSearch;
    });

    if (sortBy === "price-low") {
      result = [...result].sort((a, b) => getPrice(a) - getPrice(b));
    }

    if (sortBy === "price-high") {
      result = [...result].sort((a, b) => getPrice(b) - getPrice(a));
    }

    if (sortBy === "title") {
      result = [...result].sort((a, b) =>
        String(a.title || a.name || "").localeCompare(
          String(b.title || b.name || "")
        )
      );
    }

    if (sortBy === "newest") {
      result = [...result].sort((a, b) => getCreatedTime(b) - getCreatedTime(a));
    }

    return result;
  }, [products, urlFilters, category, searchText, sortBy]);

  const categories = useMemo(() => {
    const categoryMap = new Map();

    products.forEach((product) => {
      const slug = getProductCategorySlug(product);
      const name = getValue(product, [
        "category",
        "category_name",
        "categoryName",
      ]);

      if (slug) {
        categoryMap.set(slug, name || prettifySlug(slug));
      }
    });

    return [...categoryMap.entries()]
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  const activeCategoryLabel = useMemo(() => {
    if (!categoryParam) return "";

    const fromProducts = products.find(
      (product) =>
        getProductCategorySlug(product) === normalizeSlug(categoryParam)
    );

    return (
      getValue(fromProducts, ["category", "category_name", "categoryName"]) ||
      prettifySlug(categoryParam)
    );
  }, [products, categoryParam]);

  const activeCollectionLabel = useMemo(() => {
    if (!collectionParam) return "";

    const fromProducts = products.find(
      (product) =>
        getProductCollectionSlug(product) === normalizeSlug(collectionParam)
    );

    return (
      getValue(fromProducts, [
        "collection_name",
        "collectionName",
        "collection",
      ]) || prettifySlug(collectionParam)
    );
  }, [products, collectionParam]);

  const activeFilterType = collectionParam
    ? "Collection"
    : categoryParam
    ? "Category"
    : "";

  const activeFilterLabel = collectionParam
    ? activeCollectionLabel
    : categoryParam
    ? activeCategoryLabel
    : "";

  const pageTitle = collectionParam
    ? activeCollectionLabel
    : categoryParam
    ? activeCategoryLabel
    : "Shop original photography.";

  const pageSubtitle = collectionParam
    ? `Showing original MayaChitro photos from the ${activeCollectionLabel} collection.`
    : categoryParam
    ? `Showing original MayaChitro photos from the ${activeCategoryLabel} category.`
    : "Explore story-led photographs from Bangladesh and beyond, available as high-resolution downloads with clear personal, editorial, and commercial licensing.";

  const updateCategory = (value) => {
    const next = {};

    if (value) next.category = value;

    setCategory(value);
    setSearchText("");
    setSearchParams(next);
  };

  const clearMainFilter = () => {
    setCategory("");
    setSearchText("");
    setSearchParams({});
  };

  const clearAllFilters = () => {
    setCategory("");
    setSearchText("");
    setSearchParams({});
  };

  return (
    <main
      className={`shop-page maya-shop-only ${
        hasActiveUrlFilter ? "shop-page-filtered" : ""
      }`}
    >
      <section
        className={`shop-hero ${
          hasActiveUrlFilter ? "shop-hero-compact" : ""
        }`}
        style={{
          "--shop-hero-image": SHOP_HERO_IMAGE_SET,
        }}
      >
        <div className="shop-hero-inner">
          <p className="eyebrow">MayaChitro Photography</p>

          <h1>{pageTitle}</h1>

          <span className="shop-title-rule" aria-hidden="true" />

          <p>{pageSubtitle}</p>

          {activeFilterLabel && (
            <div className="shop-active-collection">
              <span>{activeFilterType}</span>
              <strong>{activeFilterLabel}</strong>

              <button type="button" onClick={clearMainFilter}>
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="shop-filter-shell" id="photograph-catalog" aria-label="Shop filters">
        <div className="shop-controls">
          <div className="shop-catalog-heading">
            <p className="eyebrow">The complete collection</p>
            <h2>Find your photograph</h2>
          </div>

          <div className="shop-control-fields">
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search by subject, place or mood"
              aria-label="Search MayaChitro photos"
            />

            <select
              value={category}
              onChange={(event) => updateCategory(event.target.value)}
              aria-label="Filter by category"
            >
              <option value="">Every category</option>

              {categories.map((item) => (
                <option value={item.slug} key={item.slug}>
                  {item.name}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              aria-label="Sort products"
            >
              <option value="newest">Newest first</option>
              <option value="title">Title A–Z</option>
              <option value="price-low">Price: low to high</option>
              <option value="price-high">Price: high to low</option>
            </select>
          </div>
        </div>

        <div className="shop-results-meta">
          <span>
            {loading
              ? "Loading MayaChitro photos..."
                : `${filteredProducts.length} photograph${
                  filteredProducts.length === 1 ? "" : "s"
                }`}
          </span>

          {activeFilterLabel && !loading && (
            <span className="shop-results-filter-note">
              Filtered by: {activeFilterLabel}
            </span>
          )}
        </div>
      </section>

      <section className="shop-grid-wrap">
        {loading && (
          <div className="shop-status">
            Loading MayaChitro products from MySQL database...
          </div>
        )}

        {error && <div className="shop-error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="shop-grid">
              {filteredProducts.map((product) => (
                <ProductCard
                  product={product}
                  hidePrice={hidePriceParam}
                  key={
                    product.id ||
                    product.productId ||
                    product.product_id ||
                    product.slug
                  }
                />
              ))}
            </div>

            {!filteredProducts.length && (
              <div className="shop-empty">
                <h2>No MayaChitro products found</h2>
                <p>
                  No products matched this filter. Check the product brand_slug,
                  category_slug, or collection_slug in phpMyAdmin.
                </p>

                <div className="shop-empty-actions">
                  <button type="button" onClick={clearAllFilters}>
                    Clear all filters
                  </button>

                  <Link to="/collections">Browse Collections</Link>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

export default Shop;
